import { useEffect, useState } from 'react';

/**
 * Debounces a value by delaying updates until after the specified delay has elapsed
 * since the last change. Useful for search inputs to avoid excessive server requests.
 *
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 500ms)
 * @returns The debounced value
 *
 * @example
 * const [searchText, setSearchText] = useState('');
 * const debouncedSearchText = useDebounce(searchText, 500);
 *
 * // Use debouncedSearchText for API calls
 * useEffect(() => {
 *   if (debouncedSearchText) {
 *     fetchResults(debouncedSearchText);
 *   }
 * }, [debouncedSearchText]);
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	useEffect(() => {
		// Set up the timeout
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		// Clean up the timeout if value changes before delay expires
		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
}
